import React from "react";
import PropTypes from "prop-types";
import { Info, Triangle } from "lucide-react";
import { Tooltip } from "@mui/material";

import { clx } from "../../../utils/Helpers";
import { UIChip } from "../../ui";

import "./StatCard.scss";

export const STAT_CARD_TONES = ["violet", "blue", "green", "amber", "red"];

/**
 * Dashboard metric tile — the icon chip and the headline value facing each
 * other across the top, the metric label below it, and then either a
 * month-over-month change line or, when the metric has no data yet, a
 * supporting message in its place.
 *
 * Passing `breakdown` switches the card to the applicants layout (Figma
 * 12931:32211): the rows replace the change line.
 */
const StatCard = ({
  icon: Icon,
  tone,
  value,
  valueSuffix,
  label,
  info,
  change,
  emptyMessage,
  emptyTone,
  breakdown,
  tag,
}) => {
  const hasBreakdown = breakdown.length > 0;

  const renderFooter = () => {
    if (hasBreakdown) {
      return (
        <ul className="stat-card__breakdown">
          {breakdown.map((row) => (
            <li key={row.id} className="stat-card__breakdown-row">
              <span className="stat-card__breakdown-value">
                {row.value}  
              </span>
              <span>{row.label} </span>
            </li>
          ))}
        </ul>
      );
    }

    if (!change) {
      return (
        <span
          className={clx(
            "stat-card__change",
            `stat-card__change--${emptyTone}`,
          )}
        >
          {emptyMessage}
        </span>
      );
    }

    return (
      <span
        className={clx(
          "stat-card__change",
          `stat-card__change--${change.tone}`,
        )}
      >
        <Triangle
          className={clx(
            "stat-card__arrow",
            change.direction === "down" && "stat-card__arrow--down",
          )}
          size={9}
          fill="currentColor"
          strokeWidth={0}
          aria-hidden="true"
        />
        <bdi dir="ltr">{change.label}</bdi>
      </span>
    );
  };

  return (
    <article
      className={clx("stat-card", hasBreakdown && "stat-card--breakdown")}
    >
      <div className="stat-card__head">
        <span className={clx("stat-card__icon", `stat-card__icon--${tone}`)}>
          {/* Figma strokes the 17px glyph at 1.417; lucide draws on a 24 viewBox,
              so 2 × 17/24 lands on the same optical weight. */}
          <Icon size={17} strokeWidth={2} aria-hidden="true" />
        </span>

        <span className="stat-card__value-group">
          <span className="stat-card__value">
            {value}
            {valueSuffix && (
              <span className="stat-card__value-suffix">{valueSuffix}</span>
            )}
          </span>

        </span>
      </div>
      <div className="stat-card__body">
        <span className="stat-card__label-row">
          <span className="stat-card__label">
            {label}
            {info && (
              <Tooltip title={info} placement="top" arrow>
                <span className="stat-card__info" tabIndex={0} aria-label={info}>
                  <Info size={13} strokeWidth={2} aria-hidden="true" />
                </span>
              </Tooltip>
            )}
          </span>

          {tag && (
            <UIChip
              appearance="light"
              size="small"
              color={tag.color}
              label={
                <>
                  {tag.value != null && (
                    <span style={{display: "inline-flex"}}>
                      <span>{tag.value}</span>
                      <span>%</span>
                    </span>
                  )}
                  <span>{tag.label}</span>
                </>
              }
              className="stat-card__tag"
            />
          )}
        </span>

        {renderFooter()}
      </div>
    </article>
  );
};

StatCard.propTypes = {
  /** Lucide icon component rendered inside the tinted chip. */
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.oneOf(STAT_CARD_TONES).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Rendered in its own span after `value` (e.g. the "%" on Attendance Rate). */
  valueSuffix: PropTypes.string,
  label: PropTypes.string.isRequired,
  /** Tooltip text of an info icon after the label, e.g. "Ongoing events only". */
  info: PropTypes.string,
  /** Month-over-month movement. Omit to fall back to `emptyMessage`. */
  change: PropTypes.shape({
    label: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(["up", "down"]).isRequired,
    tone: PropTypes.oneOf(["positive", "negative"]).isRequired,
  }),
  emptyMessage: PropTypes.string,
  emptyTone: PropTypes.oneOf(["muted", "positive"]),
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /**
   * Optional chip beside the label (e.g. "62% Saudi" on Yoram Applicants):
   * `value` is the percentage, rendered with its "%" in a span of its own.
   */
  tag: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};

StatCard.defaultProps = {
  valueSuffix: null,
  info: "",
  change: null,
  emptyMessage: "",
  emptyTone: "muted",
  breakdown: [],
  tag: null,
};

export default StatCard;
